/**
 * API for explicitly signalling an AI Guide events to the mobile app APIs and
 * to listen for AI Guide commands from the mobile app.
 */
import type {
    V1GuideToApp,
    V1GuideToWeb,
} from "@khanacademy/embedded-webapp-schema";
import {listenForSignal, sendSignal} from "@khan/integrations";

import type {OpenedState} from "./types.ts";
import {uxStack} from "./ux-stack-singleton.ts";
import {createBreadcrumb} from "./ux-stack.ts";

/**
 * Slugs for the current UX that exists.
 *
 * This is a convenience type to help avoid typos. If a new UX is included
 * such as the languages dropdown in preferences, then it can be added here
 * without worrying about the mobile app side.
 *
 * Uses a simple union over an enum for ease of reference/use as a compromise
 * (otherwise, it ends up being an auto-import problem and long dereferencing
 * path due to the choice of exposing APIs via a `MobileApp` namespace import).
 */
export type UXSlug =
    | "header-menu"
    | "preferences"
    | "feedback-form"
    | "math-input"
    | "guide-closet"
    | "guide-text-to-speech-onboarding-intro"
    | "guide-text-to-speech-onboarding-voice-selection";

export interface CloseableState {
    /**
     * A translated string to title the UX.
     *
     * When not provided, the app integrating webapp will use a title
     * relevant to that app. For example, the title of the mobile app page
     * that hosts the embedded webapp.
     */
    translatedTitle?: string;

    /**
     * A translated string to label the close action.
     *
     * When not provided, the app integrating webapp will use a default
     * label for the Close action.
     */
    translatedCloseLabel?: string;
}

export interface ConfirmableState {
    /**
     * The validity state of the UX.
     *
     * When `true`, the UX is valid and the user can confirm the data entered.
     * When `false`, the UX is invalid and the user cannot confirm the data
     * entered. The app integrating webapp can disable/enable the confirmation
     * action based on this value.
     */
    canConfirm: boolean;

    /**
     * A translated string to title the UX.
     *
     * When not provided, the app integrating webapp will use a title
     * relevant to that app. For example, the title of the mobile app page
     * that hosts the embedded webapp.
     */
    translatedTitle?: string;

    /**
     * A translated string to label the cancel action.
     *
     * When not provided, the app integrating webapp will use a default
     * label for the Cancel action.
     */
    translatedCancelLabel?: string;

    /**
     * A translated string to label the confirm action.
     *
     * When not provided, the app integrating webapp will use a default
     * label for the Confirm action.
     */
    translatedConfirmLabel?: string;
}

interface UXOpenedFn {
    /**
     * Signal that the guide opened or updated a UX context.
     *
     * This is used to tell the mobile app that the UX context has changed to
     * a showing dismissible UX, enabling the mobile app to modify the
     * back behavior accordingly.
     *
     * @param slug The slug of the UX that was opened.
     * @param behavior The behavior to use when the UX is dismissed.
     * @param [state] Optional state that the app integrating webapp can use
     * to customize the experience.
     * @throws if no close listener is registered for the given slug. Any
     * UX participating in the stack must register for the close signal.
     */
    (slug: UXSlug, behavior: "close", state?: CloseableState): void;

    /**
     * Signal that the guide opened or updated a UX context.
     *
     * This is used to tell the mobile app that the UX context has changed to
     * a showing dismissible UX, enabling the mobile app to modify the
     * back behavior accordingly.
     *
     * @param slug A descriptive unique slug identifying the new active context.
     * @param behavior The behavior for dismissing the new active UX
     * context.
     * @param [state] Optional state that the app integrating webapp can use
     * to customize the experience.
     * @throws if no close listener is registered for the given slug. Any
     * UX participating in the stack must register for the close signal.
     */
    (
        slug: UXSlug,
        behavior: "confirm-or-cancel",
        state?: ConfirmableState,
    ): void;
}

export const uxOpened: UXOpenedFn = function uxOpened(
    slug: UXSlug,
    behavior: V1GuideToApp.DismissBehavior,
    state: CloseableState | ConfirmableState = {},
): void {
    const stackState: OpenedState = {};
    if ("canConfirm" in state && state.canConfirm != null) {
        stackState.canConfirm = state.canConfirm;
    }
    if (state?.translatedTitle != null) {
        stackState.translatedTitle = state.translatedTitle;
    }
    if ("translatedConfirmLabel" in state) {
        stackState.translatedActionLabels ||= {};
        stackState.translatedActionLabels.ok = state.translatedConfirmLabel;
    }
    const maybeCanConfirmOrCloseLabel =
        "translatedCloseLabel" in state
            ? state.translatedCloseLabel
            : "translatedCancelLabel" in state
              ? state.translatedCancelLabel
              : undefined;
    if (maybeCanConfirmOrCloseLabel != null) {
        stackState.translatedActionLabels ||= {};
        stackState.translatedActionLabels.cancel = maybeCanConfirmOrCloseLabel;
    }

    uxStack().opened(createBreadcrumb(slug, behavior, stackState));
};

/**
 * Signal that the guide closed a UX context.
 *
 * This is used to tell the mobile app that the UX context has changed to
 * a showing dismissible UX, enabling the mobile app to modify the
 * back behavior accordingly.
 *
 * @param slug The slug of the UX that was closed.
 */
export function uxClosed(slug: UXSlug) {
    uxStack().closed(slug);
}

/**
 * Signal a change to the remaining usage quota for the user.
 *
 * A value from 0 to 1, where 0 is no remaining usage and 1 is 100%
 * remaining usage. If the value is nullish, then the usage quota is
 * unknown.
 *
 * This is used to tell the mobile app that the remaining usage quota has
 * changed, enabling the mobile app to display remaining usage.
 *
 * @param remainingQuota The remaining usage quota for the user.
 */
export function updateRemainingQuota(remainingQuota?: number | null): void {
    sendSignal({
        type: "guide:remainingQuota",
        data: {
            remainingQuota,
        },
    });
}

/**
 * Listen for the mobile app signal that open UX should be closed.
 *
 * @param slug The slug of the UX for which this event handler is listening.
 * @param cb The callback to invoke when the signal is received.
 * @returns A function that can be used to unsubscribe from the signal.
 * @throws if a close listener is already registered for the given slug.
 */
export function onCloseUX(
    slug: UXSlug,
    cb: (action: V1GuideToWeb.DismissAction) => void,
): () => void {
    return uxStack().listenForClose(slug, cb);
}

/**
 * Listen for the mobile app signal that the guide menu should be toggled.
 *
 * @param cb The callback to invoke when the signal is received.
 * @returns A function that can be used to unsubscribe from the signal.
 */
export function onToggleMenu(
    cb: (data: V1GuideToWeb.ToggleMenu["data"]) => void,
): () => void {
    return listenForSignal<V1GuideToWeb.ToggleMenu>("guide:toggleMenu", cb);
}
